import { Component, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthService,
  CustomValidators,
  NavigateService,
  PersonalService,
  SessionService,
  UtilsService,
  showSuccess,
  FeedMessageService,
  LoadingService,
} from '@services/index.service';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, of, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { Router } from '@angular/router';
import { catchError, filter, finalize, map, switchMap, tap } from 'rxjs/operators';
import { AngularPaths, Consumer, CustomdataResponse, PasswordRequest, Response } from '@models/index.model';

@Component({
  selector: 'app-password-update',
  templateUrl: './password-update.component.html',
  standalone: false
})
export class PasswordUpdateComponent implements OnInit, OnDestroy {
  public theme = environment.THEME;
  public tooltipTitle: any;
  public form: UntypedFormGroup;
  public loadingLogin = false;

  // GET CUSTOM SETTING
  public customImgSrc: string;
  public customText: string;

  private updateStateLabel: string = '';

  public data: BehaviorSubject<Object>;

  private _routerParams: { username: string; update: boolean };
  private _formSubmitAttempt: boolean;

  // GET PUBLIC STATU
  private _updateStage: string = '';
  private _updateState: string = '';
  private _validLicense: boolean = null;
  private _updateStageLabel: string = '';

  private _intervalStatus: any;
  public isMobile: boolean;
  public currentConsumer: Consumer;

  private _newData = {
    updateStage: this._updateStage,
    updateState: this._updateState,
    validLicense: this._validLicense,
    updateStageLabel: this._updateStageLabel,
    updateStateLabel: this.updateStateLabel,
  };

  constructor(
    public dialog: MatDialog,
    public _utils: UtilsService,
    private _notify: FeedMessageService,
    private _loading: LoadingService,
    private _titleSvc: Title,
    private _fb: UntypedFormBuilder,
    private _personalSvc: PersonalService,
    private _translate: TranslateService,
    private _router: Router,
    private _navigate: NavigateService,
    private _session: SessionService,
    private _auth: AuthService
  ) {
    this.theme = this._utils.titleCaseWord(this.theme);
    this.isMobile = environment.platform === 'mobile';
    this._routerParams = this._navigate.getParams(this._router);
    this._translate.get(['GLOBAL.TOOLTIP']).subscribe(res => {
      this.tooltipTitle = res['GLOBAL.TOOLTIP'];
    });
    this.data = new BehaviorSubject<Object>(this._newData);
    if (this.isMobile) {
      this.currentConsumer = Consumer.SIGNTRUST_APP;
    } else {
      this.currentConsumer = Consumer.PERSONAL_AREA;
    }
  }

  ngOnInit() {
    this._titleSvc.setTitle(`${this._translate.instant('LOGIN.FORM.CHANGE-PASSWORD')} - ${this.theme}`);
    this._getCustomData();
    this.form = this._createUpdatePasswordForm(this._routerParams?.username);
    this.dialog.closeAll();
  }

  private _getCustomData() {
    this._personalSvc
      .getCustomData()
      .pipe(
        filter((res: Response<any>) => !!res.data),
        map((res: Response<any>) => res.data)
      )
      .subscribe((res: CustomdataResponse) => {
        this.customImgSrc = res.b64ImageData ? this._utils.parseText64ToImgURL(res.b64ImageData, 'png') : null;
        this.customText = res.customText ? res.customText : null;
      });
  }

  private _createUpdatePasswordForm(username: string): UntypedFormGroup {
    return this._fb.group(
      {
        username: [{ value: username, disabled: true }, Validators.required],
        password: [null, Validators.required],
        newPassword: [null, Validators.required],
        newPasswordConfirm: [null, Validators.required],
      },
      {
        validators: CustomValidators.fieldsMatch(['newPassword', 'newPasswordConfirm']),
      }
    );
  }

  public isFieldInvalid(field: string): boolean {
    const fieldControl: AbstractControl = this.form.get(field);
    return (!fieldControl?.valid && fieldControl?.touched) || (fieldControl?.untouched && this._formSubmitAttempt);
  }

  public onSubmit(): void {
    this.loadingLogin = true;
    const passwordFormValue: PasswordRequest = this.form.getRawValue();
    this._auth
      .changePassword({
        username: passwordFormValue.username,
        password: passwordFormValue.password,
        newPassword: passwordFormValue.newPassword,
      })
      .pipe(
        tap(_ => {
          this._formSubmitAttempt = true;
          this._notify.feedBackResult(_.messageType, _.message, showSuccess);
        }),
        switchMap(_ =>
          this._session
            .initLocalUserSession({
              username: passwordFormValue.username,
              consumer: this.currentConsumer,
              password: passwordFormValue.newPassword,
            })
        ),
        catchError(err => {
          this._cleanPassword();
          return throwError(() => err);
        }),
        finalize(() => {
          this.loadingLogin = false;
        })
      )
      .subscribe();
  }

  private _cleanPassword(): void {
    Object.keys(this.form.controls)
      .filter((keyName: string) => keyName !== 'username')
      .forEach((keyName: string) => {
        const control = this.form.get(keyName);
        control.reset();
        control.updateValueAndValidity();
      });
  }

  public returnLogin(): void {
    this._navigate.setUrl(`/${AngularPaths.LOGIN}`);
  }

  public ngOnDestroy(): void {
    clearInterval(this._intervalStatus);
  }
}
